import React, { useCallback, useMemo, useState } from "react";
import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Message } from "components/SnackbarMessage";
import { MessageContext } from "utils/MessageProvider";
import { isLastRunningPodByName } from "./podDeletion";
import { CLUSTER_ACTION_TYPE, LINEAR_PROGRESS_INDEX } from "constants/common";
import {
  ClusterActionTypeOptionsProp,
  ClusterType,
  CustomerEnvironment,
  isWorkerHostingEnvironment,
  Pod,
  RolloutRestartType,
} from "utils/saastypes";
import AlertDialog from "components/AlertDialog";
import ScaleDeploymentDialog from "components/dialogs/ScaleDeploymentDialog";
import UpgradeDeploymentDialog from "components/dialogs/UpgradeDeployment/UpgradeDeploymentDialog";
import RollbackDeploymentDialog from "components/dialogs/RollbackDeploymentDialog";
import { Box } from "@mui/system";
import { ClearRounded } from "@mui/icons-material";
import AddEKSToAgentDialog from "components/dialogs/AddEKSToAgentDialog";
import UploadCertificateDialog from "components/dialogs/UploadCertificateDialog";
import RolloutRestartDialog from "components/dialogs/RolloutRestartDialog";
import ChangeEnvironmentTypeDialog from "components/dialogs/ChangeEnvironmentTypeDialog";
import ChangeTelemetryDialog from "components/dialogs/ChangeTelemetryDialog";
import ApiGatewayToggleDialog from "components/dialogs/ApiGatewayToggleDialog";
import UpdateKubernetesDialog from "components/dialogs/UpdateKubernetesDialog";
import UpdateDeploymentMemoryDialog from "components/dialogs/UpdateDeploymentMemoryDialog";
import {
  useActionWithPath,
  useAgentLogs,
  useAuthHeaders,
  useClusterListing,
  useDeploymentInfo,
  useFetchCompatibilityList,
} from "utils/query";
import { eligiblePeers } from "utils/multiRegion";
import { AGENT_COMMAND_PATH, DEPLOY_METRICS_PROXY_PATH } from "utils/apiPaths";
import {
  contextFlagOptions,
  fetchOptions,
  INTEGER_AT_LEAST_ONE_NUMBER,
  rolloutRestartResourceBaseTypes,
} from "./constants";
import { CLUSTER_DETAIL_URL } from "utils/routePaths";

interface ActionTypeSelectorProps {
  isOrkesUser: boolean;
  /** COMPANY_ADMIN or COMPANY_ROOT: the other half of CommandAccess.CUSTOMER_MANAGE. */
  hasAdminOrRootRole: boolean;
  /** environmentManagement || globalBypass. */
  canManageEnvironment: boolean;
  /** globalBypass || supportOperationalAccess. */
  canUseSupportOperations: boolean;
  /** Orkes-organization membership or the Engineering assumed profile. */
  hasGlobalBypass: boolean;
  /** CommandAccess.ORKES_ENGINEERING. */
  hasEngineeringProfile: boolean;
  clusterDetail: CustomerEnvironment | undefined;
  refetchClusterDetail: () => void;
  selectedActionType: string;
  podName: string;
  /** From the Pods tab, which sees refreshed pods the cached deployment info does not. */
  deletingLastRunningPod?: boolean;
  handleSelectedActionType: (val: string) => void;
  handleChangePodName: (val: string) => void;
}

// Worker hosting (CLOUD-1193): an allowlist, deliberately, and for the same reason as
// WORKER_HOSTING_TABS. A worker environment is a reduced deployment, so a newly added action
// should default to hidden here rather than appear and fail against infrastructure that does not
// exist. Everything below acts on pods, deployments or the AKS cluster, all real on this path.
//
// Excluded, with the reason, since the list is easier to review than to re-derive:
//   multiregion peer / promote / unpeer   no Conductor server to replicate
//   run SQL in Conductor                  no Postgres
//   enable / disable API gateway          no ingress
//   upload certificate                    no TLS termination here
//   deploy metrics proxy                  no Prometheus is deployed
//   deploy MCP worker / assistant / otel  server-side features
//   add EKS administrator                 AWS only, already gated on ENTERPRISE
//   generate agent role update script     worker hosting never runs aks-create-roles.sh
const WORKER_HOSTING_ACTIONS: string[] = [
  CLUSTER_ACTION_TYPE.UPGRADE_DEPLOYMENT,
  CLUSTER_ACTION_TYPE.SCALE_DEPLOYMENT,
  CLUSTER_ACTION_TYPE.ROLLOUT_RESTART,
  CLUSTER_ACTION_TYPE.ROLLBACK_DEPLOYMENT,
  CLUSTER_ACTION_TYPE.UPDATE_DEPLOYMENT_MEMORY,
  CLUSTER_ACTION_TYPE.DELETE_POD,
  CLUSTER_ACTION_TYPE.GET_PODS_NAMES,
  CLUSTER_ACTION_TYPE.GET_DEPLOYMENTS_INFO,
  CLUSTER_ACTION_TYPE.GET_TOP_OUTPUT_FROM_POD,
  CLUSTER_ACTION_TYPE.PULL_LOGS,
  CLUSTER_ACTION_TYPE.DOWNLOAD_ALL_POD_LOGS,
  CLUSTER_ACTION_TYPE.DOWNLOAD_HEAP_DUMP,
  CLUSTER_ACTION_TYPE.DOWNLOAD_THREAD_DUMP,
  CLUSTER_ACTION_TYPE.DEPLOY_DEBUGGER_POD,
  CLUSTER_ACTION_TYPE.KUBECTL_UNRESTRICTED,
  CLUSTER_ACTION_TYPE.HEALTH_CHECK,
  CLUSTER_ACTION_TYPE.CHANGE_TELEMETRY_STATE,
  CLUSTER_ACTION_TYPE.CHANGE_ENVIRONMENT_TYPE,
  CLUSTER_ACTION_TYPE.UPDATE_KUBERNETES,
];

function ActionTypeSelector({
  isOrkesUser,
  hasAdminOrRootRole,
  canManageEnvironment,
  canUseSupportOperations,
  hasGlobalBypass,
  hasEngineeringProfile,
  clusterDetail,
  selectedActionType,
  podName,
  deletingLastRunningPod,
  refetchClusterDetail,
  handleSelectedActionType,
  handleChangePodName,
}: ActionTypeSelectorProps) {
  const navigate = useNavigate();
  const { clusterName } = useParams();
  const canRunCustomerManageCommand = isOrkesUser || hasAdminOrRootRole;
  // Rollback is a CUSTOMER_MANAGE agent command, but its dialog first reads version history from
  // an environmentManagement || globalBypass endpoint. Both predicates must hold.
  const canRollbackDeployment =
    canRunCustomerManageCommand && canManageEnvironment;
  const canManageCertificates = canManageEnvironment || canUseSupportOperations;
  const { refetch: refetchLogs } = useAgentLogs(clusterName as string);
  const { data: compatibilityVersionList } = useFetchCompatibilityList();
  const authHeaders = useAuthHeaders();

  const { data: deploymentInfo } = useDeploymentInfo(clusterDetail?.name ?? "");

  const podsData = deploymentInfo?.pods_data ?? [];
  const podsNames = podsData.map((item: Pod) => item?.name);
  // What the Pods tab said, if it said anything: it sees refreshed pods this cache does not.
  const deletingOnlyRunningPod =
    deletingLastRunningPod ?? isLastRunningPodByName(podName, podsData);

  // Add options to rollout restart options
  const rolloutRestartResourceTypes = useMemo<
    {
      name: string;
      value: RolloutRestartType;
    }[]
  >(() => {
    const isWorkerHostingCluster = isWorkerHostingEnvironment(clusterDetail);
    const isServerCluster =
      (clusterDetail?.hostedWorkerEnvironments?.length ?? 0) > 0;

    if (isWorkerHostingCluster) {
      return rolloutRestartResourceBaseTypes.filter((x) =>
        [RolloutRestartType.WORKERS, RolloutRestartType.AGENT].includes(x.value)
      );
    }

    const result = isServerCluster
      ? rolloutRestartResourceBaseTypes.filter(
          (x) => x.value !== RolloutRestartType.WORKERS
        )
      : [...rolloutRestartResourceBaseTypes];

    if (
      !isWorkerHostingCluster &&
      clusterDetail &&
      [ClusterType.STANDARD, ClusterType.ENTERPRISE].includes(
        clusterDetail.type
      )
    ) {
      result.push({
        name: "Metrics Proxy",
        value: RolloutRestartType.METRICS_PROXY,
      });
    }

    if (clusterDetail?.appVersions?.mcpWorker) {
      result.push({
        name: "MCP Workers",
        value: RolloutRestartType.MCP_WORKER,
      });
    }

    return result;
  }, [clusterDetail]);

  const [selectedRestartResourceType, setSelectedRestartResourceType] =
    useState(rolloutRestartResourceTypes[0]);

  const { pathname } = useLocation();
  const basePath = `${CLUSTER_DETAIL_URL.ROOT}/${clusterName}`;
  const relativePath = pathname.split(basePath)[1];
  const [grep, setGrep] = useState("");
  const [sqlQuery, setSqlQuery] = useState("");
  const [unrestrictedCommand, setUnrestrictedCommand] = useState("");
  const [expectedRowCount, setExpectedRowCount] = useState("");
  const [transactionalQuery, setTransactionalQuery] = useState(true);
  const [contextLength, setContextLength] = useState("1");
  const [contextFlag, setContextFlag] = useState("N");

  const [fetchOption, setFetchOption] = useState("tail");

  const [startLine, setStartLine] = useState("");

  const [lines, setLines] = useState("100");
  // Multi-region peering inputs (CLOUD-1109)
  const [peerCluster, setPeerCluster] = useState("");
  const [peerThisIsPrimary, setPeerThisIsPrimary] = useState(true);
  // Immutable for the lifetime of the pairing, so it is collected once here and never offered for edit.
  const [multiRegionName, setMultiRegionName] = useState("");
  const [newMultiRegionName, setNewMultiRegionName] = useState("");
  const [newClusterDomain, setNewClusterDomain] = useState("");
  // Suggestions for the peer field. The listing is already fetched and cached for the app bar, so this
  // adds no request. The field stays freeSolo: these are suggestions, and the server is the authority.
  const { data: clusterListing } = useClusterListing();
  const peerOptions = useMemo(
    () => eligiblePeers(clusterListing, clusterDetail?.name),
    [clusterListing, clusterDetail?.name]
  );

  const [forceDelete, setForceDelete] = useState(false);

  const [mcpWorkerVersion, setMcpWorkerVersion] = useState("");
  const [otelChartVersion, setOtelChartVersion] = useState("");
  const [otelEnableOnConductor, setOtelEnableOnConductor] = useState(false);
  const [anthropicApiKey, setAnthropicApiKey] = useState("");
  const [assistantVersion, setAssistantVersion] = useState("");

  const { setMessage } = React.useContext(MessageContext);

  const isWorkerHosting = isWorkerHostingEnvironment(clusterDetail);

  const showError = (message: string) => {
    setMessage(new Message(message, "error"));
  };

  const showSuccess = (message: string) => {
    setMessage(new Message(message, "success"));
  };

  const clusterActionTypeOptions: ClusterActionTypeOptionsProp[] = [
    {
      label: "Upgrade Deployment",
      value: CLUSTER_ACTION_TYPE.UPGRADE_DEPLOYMENT,
      hidden: !canRunCustomerManageCommand,
    },
    {
      label: "Scale Deployment",
      value: CLUSTER_ACTION_TYPE.SCALE_DEPLOYMENT,
      hidden: !isOrkesUser,
    },
    {
      label: "Add EKS Administrator",
      value: CLUSTER_ACTION_TYPE.ADD_EKS_ADMIN,
      hidden: !isOrkesUser || clusterDetail?.type !== ClusterType.ENTERPRISE,
    },
    {
      // Peer only a standalone cluster (not already part of a pair)
      label: "MultiRegion - Peer",
      value: CLUSTER_ACTION_TYPE.MAKE_MULTIREGION_PEER,
      hidden: Boolean(clusterDetail?.replicationRole),
      disabled: !canRunCustomerManageCommand,
    },
    {
      // Promote only a REPLICA
      label: "MultiRegion - Promote to Primary",
      value: CLUSTER_ACTION_TYPE.PROMOTE_TO_PRIMARY,
      hidden: clusterDetail?.replicationRole !== "REPLICA",
      disabled: !canRunCustomerManageCommand,
    },
    {
      // Unpeer only a cluster that is currently peered
      label: "MultiRegion - Unpeer",
      value: CLUSTER_ACTION_TYPE.UNPEER_CLUSTERS,
      hidden: !clusterDetail?.replicationRole,
      disabled: !canRunCustomerManageCommand,
    },
    {
      // Rename only a pair that actually has an endpoint to rename
      label: "MultiRegion - Rename Endpoint",
      value: CLUSTER_ACTION_TYPE.RENAME_MULTIREGION_ENDPOINT,
      hidden: !clusterDetail?.multiRegionName,
      disabled: !canRunCustomerManageCommand,
    },
    {
      label: "Replace Cluster Domain",
      value: CLUSTER_ACTION_TYPE.REPLACE_CLUSTER_DOMAIN,
      hidden: !isOrkesUser,
    },
    {
      label: "Pull Pod Logs",
      value: CLUSTER_ACTION_TYPE.PULL_LOGS,
      hidden: !canRunCustomerManageCommand,
    },
    {
      label: "Get Deployments Info",
      value: CLUSTER_ACTION_TYPE.GET_DEPLOYMENTS_INFO,
      hidden: true,
    },
    {
      label: "Health check",
      value: CLUSTER_ACTION_TYPE.HEALTH_CHECK,
      hidden: true,
    },
    {
      label: "Get Pod Names",
      value: CLUSTER_ACTION_TYPE.GET_PODS_NAMES,
      hidden: true,
    },
    {
      label: "Rollout Restart",
      value: CLUSTER_ACTION_TYPE.ROLLOUT_RESTART,
      hidden: !canRunCustomerManageCommand,
    },
    {
      label: "Rollback Deployment",
      value: CLUSTER_ACTION_TYPE.ROLLBACK_DEPLOYMENT,
      hidden: !canRollbackDeployment,
    },
    {
      label: "Change Cluster Environment Type",
      value: CLUSTER_ACTION_TYPE.CHANGE_ENVIRONMENT_TYPE,
      hidden: !canManageEnvironment,
    },
    {
      label: "Deploy Metrics Proxy",
      value: CLUSTER_ACTION_TYPE.DEPLOY_METRICS_PROXY,
      hidden: !hasGlobalBypass || clusterDetail?.type === ClusterType.STARTER,
    },
    {
      label: "Deploy MCP Workers",
      value: CLUSTER_ACTION_TYPE.DEPLOY_MCP_WORKER,
      hidden: !isOrkesUser || clusterDetail?.type === ClusterType.STARTER,
    },
    {
      label: "Deploy Assistant",
      value: CLUSTER_ACTION_TYPE.DEPLOY_ASSISTANT,
      hidden: !hasGlobalBypass || clusterDetail?.type === ClusterType.STARTER,
    },
    {
      label: "Deploy Otel Collector",
      value: CLUSTER_ACTION_TYPE.DEPLOY_OTEL,
      // Enterprise only, because the in-cluster agent needs cluster-scoped RBAC to create the
      // otel-collector namespace and its DaemonSet. The server additionally refuses
      // customer-hosted clusters, which is not derivable here, so that one surfaces as its error.
      hidden: !isOrkesUser || clusterDetail?.type !== ClusterType.ENTERPRISE,
    },
    {
      label: "Change Telemetry State",
      value: CLUSTER_ACTION_TYPE.CHANGE_TELEMETRY_STATE,
      hidden: !canUseSupportOperations,
    },
    {
      label: "Deploy Debugger Pod",
      value: CLUSTER_ACTION_TYPE.DEPLOY_DEBUGGER_POD,
      hidden: !isOrkesUser,
    },
    {
      label: "Upload Certificate",
      value: CLUSTER_ACTION_TYPE.UPLOAD_CERTIFICATE,
      hidden: !canManageCertificates,
    },
    {
      label: "Download all logs from Pod",
      value: CLUSTER_ACTION_TYPE.DOWNLOAD_ALL_POD_LOGS,
      hidden: !isOrkesUser,
    },
    {
      label: "Get Heap Dump",
      value: CLUSTER_ACTION_TYPE.DOWNLOAD_HEAP_DUMP,
      hidden: !isOrkesUser,
    },
    {
      label: "Download Thread Dump",
      value: CLUSTER_ACTION_TYPE.DOWNLOAD_THREAD_DUMP,
      hidden: !isOrkesUser,
    },
    {
      label: "Get Top Output From Pod",
      value: CLUSTER_ACTION_TYPE.GET_TOP_OUTPUT_FROM_POD,
      hidden: !isOrkesUser,
    },
    {
      label: "Run SQL Query in Conductor",
      value: CLUSTER_ACTION_TYPE.RUN_SQL_QUERY_IN_CONDUCTOR,
      hidden: !hasEngineeringProfile,
    },
    {
      label: "Update Deployment Memory",
      value: CLUSTER_ACTION_TYPE.UPDATE_DEPLOYMENT_MEMORY,
      hidden: !isOrkesUser,
    },
    {
      label: "Run Kubectl command",
      value: CLUSTER_ACTION_TYPE.KUBECTL_UNRESTRICTED,
      hidden: !hasEngineeringProfile,
    },
    {
      label: "Update Kubernetes",
      value: CLUSTER_ACTION_TYPE.UPDATE_KUBERNETES,
      hidden:
        !canManageEnvironment ||
        clusterDetail?.cloudProvider !== "AWS" ||
        clusterDetail?.type !== ClusterType.ENTERPRISE,
    },
    {
      label: "Enable API Gateway",
      value: CLUSTER_ACTION_TYPE.ENABLE_API_GATEWAY,
      hidden:
        !canManageEnvironment ||
        (clusterDetail?.cloudProvider !== "AWS" &&
          clusterDetail?.cloudProvider !== "AZURE" &&
          clusterDetail?.cloudProvider !== "GCP") ||
        clusterDetail?.enableApiGateway === true,
    },
    {
      label: "Disable API Gateway",
      value: CLUSTER_ACTION_TYPE.DISABLE_API_GATEWAY,
      hidden:
        !canManageEnvironment ||
        (clusterDetail?.cloudProvider !== "AWS" &&
          clusterDetail?.cloudProvider !== "AZURE" &&
          clusterDetail?.cloudProvider !== "GCP") ||
        clusterDetail?.enableApiGateway !== true,
    },
    {
      label: "Generate Agent Role Update Script",
      value: CLUSTER_ACTION_TYPE.GENERATE_AGENT_ROLE_UPDATE_SCRIPT,
      hidden:
        !canUseSupportOperations ||
        clusterDetail?.type !== ClusterType.ENTERPRISE,
    },
  ].map((option) =>
    isWorkerHosting && !WORKER_HOSTING_ACTIONS.includes(option.value)
      ? { ...option, hidden: true }
      : option
  );

  const closeDialog = () => {
    handleSelectedActionType("");
  };

  const renderActionTypeOptions = (options: ClusterActionTypeOptionsProp[]) =>
    options.map((option: ClusterActionTypeOptionsProp, index: number) =>
      option.hidden ? null : (
        <MenuItem key={index} value={option.value} disabled={option.disabled}>
          {option.label}
        </MenuItem>
      )
    );

  const deployMetricProxyAction = useActionWithPath({
    onSuccess: (data: { status?: string }) => {
      if (data?.status === "ERROR") {
        showError("Could not deploy metrics-proxy");
      } else {
        showSuccess(`Metrics-proxy have been scheduled successfully.`);
      }
    },
  });

  const handleDeployMetrics = useCallback(() => {
    if (clusterDetail) {
      // @ts-ignore
      deployMetricProxyAction.mutate({
        method: "POST",
        path: `${DEPLOY_METRICS_PROXY_PATH}?clusterName=${clusterDetail.name}`,
        errorMessage: "Could not deploy metrics-proxy:",
      });
    }
  }, [deployMetricProxyAction, clusterDetail]);

  const deployDebuggerPodAction = useActionWithPath({
    onSuccess: () => {
      refetchClusterDetail();
    },
    onError: () => {
      refetchClusterDetail();
    },
  });

  const handleDeployDebuggerPod = useCallback(() => {
    if (clusterDetail) {
      // @ts-ignore
      deployDebuggerPodAction.mutate({
        method: "post",
        path: AGENT_COMMAND_PATH,
        body: JSON.stringify({
          clusterName: clusterDetail?.name,
          command: CLUSTER_ACTION_TYPE.DEPLOY_DEBUGGER_POD,
          parameters: {},
        }),
        errorMessage: "Could not deploy debugger pod",
        successMessage: "Debugger Pod have been successfully deployed.",
      });
    }
  }, [clusterDetail, deployDebuggerPodAction]);

  const pullLogsAction = useActionWithPath({
    onSuccess: (data: any) => {
      if (data?.status === "ERROR") {
        showError("Could not pull pod logs");
      } else {
        if (data.id) {
          navigate(
            `${CLUSTER_DETAIL_URL.ROOT}/${clusterName}/${CLUSTER_DETAIL_URL.HISTORY}/${data.id}`
          );
        }
        showSuccess(`Pod logs have been scheduled successfully.`);
      }
    },
  });

  const sqlQueryAction = useActionWithPath({
    onSuccess: (data: any) => {
      if (data?.status === "ERROR") {
        showError("Could not run sql action");
      } else {
        if (data.id) {
          navigate(
            `${CLUSTER_DETAIL_URL.ROOT}/${clusterName}/${CLUSTER_DETAIL_URL.HISTORY}/${data.id}`
          );
        }
        showSuccess(`Sql command ran successfully `);
      }
    },
  });

  const deployMcpWorkerAction = useActionWithPath({
    onSuccess: (data: any) => {
      if (data?.status === "ERROR") {
        showError(data?.message || "Could not deploy MCP Workers");
      } else {
        if (data.id) {
          navigate(
            `${CLUSTER_DETAIL_URL.ROOT}/${clusterName}/${CLUSTER_DETAIL_URL.HISTORY}/${data.id}`
          );
        }
        showSuccess("Deploy MCP Workers has been scheduled successfully.");
      }
    },
  });

  const deployOtelAction = useActionWithPath({
    onSuccess: (data: any) => {
      if (data?.status === "ERROR") {
        showError(data?.message || "Could not deploy the OTel collector");
      } else {
        if (data.id) {
          navigate(
            `${CLUSTER_DETAIL_URL.ROOT}/${clusterName}/${CLUSTER_DETAIL_URL.HISTORY}/${data.id}`
          );
        }
        showSuccess("Deploy Otel Collector has been scheduled successfully.");
      }
    },
  });

  const deployAssistantAction = useActionWithPath({
    onSuccess: (data: any) => {
      if (data?.status === "ERROR") {
        showError(data?.message || "Could not deploy the Assistant");
      } else {
        showSuccess("Deploy Assistant has been scheduled successfully.");
      }
    },
  });

  const kubectlUnrestrictedAction = useActionWithPath({
    onSuccess: (data: any) => {
      if (data?.status === "ERROR") {
        showError("Could not run kubectl command");
      } else {
        if (data.id) {
          navigate(
            `${CLUSTER_DETAIL_URL.ROOT}/${clusterName}/${CLUSTER_DETAIL_URL.HISTORY}/${data.id}`
          );
        }
        showSuccess(`Kubectl command ran successfully `);
      }
    },
  });

  const downloadAllPodLogsAction = useActionWithPath({
    onSuccess: (data: any) => {
      if (data?.status === "ERROR") {
        showError("Could not download all pod logs");
      } else {
        if (data.id) {
          navigate(
            `${CLUSTER_DETAIL_URL.ROOT}/${clusterName}/${CLUSTER_DETAIL_URL.HISTORY}/${data.id}`
          );
        }
        showSuccess(`Download has been scheduled successfully.`);
      }
    },
  });

  const deletePodAction = useActionWithPath({
    onSuccess: (data: any) => {
      if (data?.status === "ERROR") {
        showError("Could not delete the pod");
      } else {
        if (data.id) {
          navigate(
            `${CLUSTER_DETAIL_URL.ROOT}/${clusterName}/${CLUSTER_DETAIL_URL.HISTORY}/${data.id}`
          );
        }
        showSuccess(`Pod deletion has been scheduled successfully.`);
      }
    },
  });

  // A rollout restart replaces every pod, which makes the cached GET_DEPLOYMENTS_INFO result the
  // Pods tab renders from immediately wrong. Nothing refetched it, so the tab kept showing pods that
  // no longer existed and pull-logs on one of them failed with
  // `Error from server (NotFound): pods "..." not found`. Observed on beta, cluster aug27: restart at
  // 16:05, pull-logs against a dead pod at 16:46. Fire a refresh so the cache catches up.
  const refreshPodsAfterRestartAction = useActionWithPath({});

  const healthCheckAction = useActionWithPath({
    onSuccess: (data: any) => {
      if (data?.status === "ERROR") {
        showError("Health check failed");
      } else {
        if (data.id) {
          navigate(
            `${CLUSTER_DETAIL_URL.ROOT}/${clusterName}/${CLUSTER_DETAIL_URL.HISTORY}/${data.id}`
          );
        }
        showSuccess(`Health check has been scheduled successfully.`);
      }
    },
  });

  // Shared mutation for the multi-region actions (peer / promote / unpeer). CLOUD-1109.
  const multiRegionAction = useActionWithPath({
    onSuccess: (data: any) => {
      if (data?.status === "ERROR") {
        showError(data?.message || "Multi-region action failed");
      } else {
        if (data.id) {
          navigate(
            `${CLUSTER_DETAIL_URL.ROOT}/${clusterName}/${CLUSTER_DETAIL_URL.HISTORY}/${data.id}`
          );
        }
        showSuccess("Multi-region action scheduled successfully.");
      }
    },
  });

  const getPodNamesAction = useActionWithPath({
    onSuccess: (data: any) => {
      if (data?.status === "ERROR") {
        showError("Pod names fetch failed");
      } else {
        if (data.id) {
          navigate(
            `${CLUSTER_DETAIL_URL.ROOT}/${clusterName}/${CLUSTER_DETAIL_URL.HISTORY}/${data.id}`
          );
        }
        showSuccess(`Pod names request has been scheduled successfully.`);
      }
    },
  });

  const getDeploymentsInfoAction = useActionWithPath({
    onSuccess: (data: any) => {
      if (data?.status === "ERROR") {
        showError("Deployment info fetch failed");
      } else {
        if (relativePath?.startsWith(`/${CLUSTER_DETAIL_URL.HISTORY}`)) {
          // refetch the logs
          refetchLogs();
        } else {
          navigate(
            `${CLUSTER_DETAIL_URL.ROOT}/${clusterName}/${CLUSTER_DETAIL_URL.HISTORY}`
          );
        }

        showSuccess(`Deployment info request has been scheduled successfully.`);
      }
    },
  });

  const downloadThreadDumpAction = useActionWithPath({
    onSuccess: (data: any) => {
      if (data?.status === "ERROR") {
        showError("Could not download thread dump.");
      } else {
        if (data.id) {
          navigate(
            `${CLUSTER_DETAIL_URL.ROOT}/${clusterName}/${CLUSTER_DETAIL_URL.HISTORY}/${data.id}`
          );
        }
        showSuccess(`Download thread dumb has been scheduled successfully.`);
      }
    },
  });

  const PodSelector = () => {
    return (
      <Box sx={{ minWidth: 120, marginTop: "16px", display: "flex" }}>
        <Box style={{ width: "100%" }}>
          <Box style={{ marginBottom: 16 }}>
            <Autocomplete
              id="select-pod-name"
              freeSolo
              options={podsData.map((option: Pod) => option.name)}
              renderInput={(params) => (
                <TextField {...params} label="Pod Name" />
              )}
              value={podName}
              onInputChange={(_event, value) => {
                handleChangePodName(value ?? "");
              }}
              onChange={(_event, value) => {
                handleChangePodName(value ?? "");
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  };
  const getHeapDumpAction = useActionWithPath({
    onSuccess: (data: any) => {
      if (data?.status === "ERROR") {
        showError("Could not fetch heap dump");
      } else {
        if (data.id) {
          navigate(
            `${CLUSTER_DETAIL_URL.ROOT}/${clusterName}/${CLUSTER_DETAIL_URL.HISTORY}/${data.id}`
          );
        }
        showSuccess(`Get heap dump has been scheduled successfully.`);
      }
    },
  });
  const getTopOutputFromPodAction = useActionWithPath({
    onSuccess: (data: any) => {
      if (data?.status === "ERROR") {
        showError("Could not fetch top output from pod");
      } else {
        if (data.id) {
          navigate(
            `${CLUSTER_DETAIL_URL.ROOT}/${clusterName}/${CLUSTER_DETAIL_URL.HISTORY}/${data.id}`
          );
        }
        showSuccess(`Get Top output from pod has been scheduled successfully.`);
      }
    },
  });

  const handleGenerateAgentRoleUpdateScript = async () => {
    if (clusterDetail) {
      try {
        const response = await fetch(
          `/api/generateAgentRoleUpdateScript?clusterName=${clusterDetail.name}`,
          {
            method: "POST",
            headers: {
              ...authHeaders,
              "Content-Type": "application/json",
              Accept: "text/plain",
            },
          }
        );

        if (!response.ok) {
          showError("Could not generate agent role update script");
          return;
        }

        const scriptContent = await response.text();
        const blob = new Blob([scriptContent], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `update-agent-role-${clusterDetail.name}.sh`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        showSuccess("Agent role update script downloaded successfully");
      } catch (error) {
        showError("Could not generate agent role update script");
      }
    }
  };

  return (
    <>
      <FormControl
        style={{
          width: 300,
          marginLeft: "auto",
          marginRight: 10,
        }}
      >
        <InputLabel id="action-type-label">Select an Action</InputLabel>
        <Select
          labelId="action-type-label"
          id="action-type-select"
          value={selectedActionType}
          label="Select an Action"
          onChange={(ev) => {
            handleSelectedActionType(ev.target.value);
          }}
          MenuProps={{
            sx: { zIndex: LINEAR_PROGRESS_INDEX + 1 },
          }}
        >
          {renderActionTypeOptions(clusterActionTypeOptions)}
        </Select>
      </FormControl>
      <>
        <AlertDialog
          textTitle="Deleting Confirmation"
          textContent={
            <>
              Please confirm if you want to proceed to delete the Pod&nbsp;
              <strong className="danger-text">{podName}</strong>. This action
              cannot be undone.
              {deletingOnlyRunningPod && (
                <>
                  <br />
                  <br />
                  This is the only running Pod of its deployment, so it will be
                  down until Kubernetes schedules a replacement.
                  <br />
                  <strong>Needs Force Delete for only pod.</strong>
                </>
              )}
            </>
          }
          open={selectedActionType === CLUSTER_ACTION_TYPE.DELETE_POD}
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();
            // @ts-ignore
            deletePodAction.mutate({
              method: "post",
              path: `/agent/command`,
              body: JSON.stringify({
                clusterName: clusterDetail?.name,
                command: CLUSTER_ACTION_TYPE.DELETE_POD,
                parameters: {
                  podName,
                  ...(forceDelete && { force: forceDelete }),
                },
              }),
              errorMessage: "Could not delete the pod:",
            });
            setForceDelete(false);
          }}
          isInputConfirmation
          // The agent refuses the last running pod without force, so confirming would only
          // produce a FAILED history entry.
          isConfirmDisabled={deletingOnlyRunningPod && !forceDelete}
          valueToBeDeleted={podName}
        >
          <Box style={{ position: "absolute", bottom: 15 }}>
            <FormControl fullWidth>
              <FormControlLabel
                label="Force delete"
                control={
                  <Checkbox
                    size="small"
                    checked={forceDelete}
                    onChange={(e) => setForceDelete(e.target.checked)}
                  />
                }
              />
            </FormControl>
          </Box>
        </AlertDialog>

        <ScaleDeploymentDialog
          open={selectedActionType === CLUSTER_ACTION_TYPE.SCALE_DEPLOYMENT}
          closeDialog={closeDialog}
          clusterName={clusterName}
          clusterDetail={clusterDetail as CustomerEnvironment}
          showError={showError}
          showSuccess={showSuccess}
          refetchLogs={refetchLogs}
        />

        <UpdateDeploymentMemoryDialog
          open={
            selectedActionType === CLUSTER_ACTION_TYPE.UPDATE_DEPLOYMENT_MEMORY
          }
          closeDialog={closeDialog}
          clusterName={clusterName}
          showError={showError}
          showSuccess={showSuccess}
          refetchLogs={refetchLogs}
        />

        {canRunCustomerManageCommand && (
          <UpgradeDeploymentDialog
            open={selectedActionType === CLUSTER_ACTION_TYPE.UPGRADE_DEPLOYMENT}
            closeDialog={closeDialog}
            clusterName={clusterName}
            clusterDetail={clusterDetail as CustomerEnvironment}
            showError={showError}
            showSuccess={showSuccess}
            refetchLogs={refetchLogs}
            compatibilityVersionList={compatibilityVersionList || []}
          />
        )}

        {/* Mounted, not merely opened, and this fetches version history immediately. */}
        {canRollbackDeployment && (
          <RollbackDeploymentDialog
            open={
              selectedActionType === CLUSTER_ACTION_TYPE.ROLLBACK_DEPLOYMENT
            }
            closeDialog={closeDialog}
            clusterName={clusterName}
            clusterDetail={clusterDetail as CustomerEnvironment}
            showError={showError}
            showSuccess={showSuccess}
            refetchLogs={refetchLogs}
          />
        )}

        <AlertDialog
          maxWidth="sm"
          textTitle="Deploy MCP Workers"
          textContent=""
          open={selectedActionType === CLUSTER_ACTION_TYPE.DEPLOY_MCP_WORKER}
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();
            // @ts-ignore
            deployMcpWorkerAction.mutate({
              method: "post",
              path: `/agent/command`,
              body: JSON.stringify({
                clusterName: clusterDetail?.name,
                command: CLUSTER_ACTION_TYPE.DEPLOY_MCP_WORKER,
                parameters: {
                  deploymentVersion: mcpWorkerVersion || "latest",
                },
              }),
              errorMessage: "Could not deploy MCP Workers",
            });
            setMcpWorkerVersion("");
          }}
        >
          Deploy MCP Workers to <strong>{clusterDetail?.name}</strong>. They run
          in the cluster's default namespace and connect to Conductor using the
          same credentials as the Conductor Workers.
          <Box sx={{ minWidth: 120, marginTop: "16px", display: "flex" }}>
            <Box style={{ width: "100%" }}>
              <FormControl fullWidth>
                <TextField
                  label="MCP Worker Version (optional)"
                  placeholder="latest"
                  value={mcpWorkerVersion}
                  onChange={(ev) => setMcpWorkerVersion(ev.target.value)}
                />
              </FormControl>
            </Box>
          </Box>
        </AlertDialog>

        <AlertDialog
          maxWidth="sm"
          textTitle="Deploy Otel Collector"
          textContent=""
          open={selectedActionType === CLUSTER_ACTION_TYPE.DEPLOY_OTEL}
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();
            // @ts-ignore
            deployOtelAction.mutate({
              method: "post",
              path: `/agent/command`,
              body: JSON.stringify({
                clusterName: clusterDetail?.name,
                command: CLUSTER_ACTION_TYPE.DEPLOY_OTEL,
                parameters: {
                  // Blank means the chart version the saas workers default to. The server
                  // validates the format, since it is interpolated into a helm reference.
                  chartVersion: otelChartVersion,
                  enableOnConductor: otelEnableOnConductor,
                },
              }),
              errorMessage: "Could not deploy the OTel collector",
            });
            setOtelChartVersion("");
            setOtelEnableOnConductor(false);
          }}
        >
          Installs the otel-pii-collector agent on{" "}
          <strong>{clusterDetail?.name}</strong> (logs, metrics and traces, PII
          redacted at source) and forwards telemetry to the Orkes observability
          platform under this cluster's tenant.
          <Box sx={{ minWidth: 120, marginTop: "16px", display: "flex" }}>
            <Box style={{ width: "100%" }}>
              <FormControl fullWidth>
                <TextField
                  label="Chart version (optional)"
                  placeholder="0.4.1"
                  value={otelChartVersion}
                  onChange={(ev) => setOtelChartVersion(ev.target.value)}
                />
              </FormControl>
            </Box>
          </Box>
          <Box sx={{ marginTop: "8px" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={otelEnableOnConductor}
                  onChange={(ev) => setOtelEnableOnConductor(ev.target.checked)}
                />
              }
              label="Enable OTel javaagent on Conductor"
            />
            <Box
              sx={{ marginLeft: "32px", marginTop: "-8px", fontSize: "12px" }}
            >
              Sets OTEL_JAVAAGENT_ENABLED=true on the server and workers
              deployments, which rolls their pods. Requires images with the
              baked-in agent.
            </Box>
          </Box>
        </AlertDialog>

        <AlertDialog
          maxWidth="sm"
          textTitle="Deploy Assistant"
          textContent=""
          open={selectedActionType === CLUSTER_ACTION_TYPE.DEPLOY_ASSISTANT}
          setOpen={closeDialog}
          isConfirmDisabled={!anthropicApiKey}
          handleConfirm={() => {
            closeDialog();
            // @ts-ignore
            deployAssistantAction.mutate({
              method: "post",
              path: `/deployAssistant?clusterName=${clusterDetail?.name}`,
              body: JSON.stringify({
                anthropicApiKey: anthropicApiKey,
                version: assistantVersion || undefined,
              }),
              errorMessage: "Could not deploy the Assistant",
            });
            setAnthropicApiKey("");
            setAssistantVersion("");
          }}
        >
          Deploy the Orkes Assistant to <strong>{clusterDetail?.name}</strong>.
          It is served at <code>/assistant</code> on the cluster URL and reuses
          the cluster's managed Postgres (pgvector) and Redis.
          <Box sx={{ minWidth: 120, marginTop: "16px", display: "flex" }}>
            <Box style={{ width: "100%" }}>
              <Box style={{ marginBottom: 16 }}>
                <FormControl fullWidth>
                  <TextField
                    label="Anthropic API Key"
                    type="password"
                    placeholder="sk-ant-..."
                    value={anthropicApiKey}
                    onChange={(ev) => setAnthropicApiKey(ev.target.value)}
                  />
                </FormControl>
              </Box>
              <FormControl fullWidth>
                <TextField
                  label="Assistant Version (optional)"
                  placeholder="latest"
                  value={assistantVersion}
                  onChange={(ev) => setAssistantVersion(ev.target.value)}
                />
              </FormControl>
            </Box>
          </Box>
        </AlertDialog>
        <AlertDialog
          maxWidth="sm"
          textTitle="Run sql query"
          textContent=""
          open={
            selectedActionType ===
            CLUSTER_ACTION_TYPE.RUN_SQL_QUERY_IN_CONDUCTOR
          }
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();
            // @ts-ignore
            sqlQueryAction.mutate({
              method: "post",
              path: `/agent/command`,
              body: JSON.stringify({
                clusterName: clusterDetail?.name,
                command: CLUSTER_ACTION_TYPE.RUN_SQL_QUERY_IN_CONDUCTOR,
                parameters: {
                  query: sqlQuery,
                  expectedRowCount: expectedRowCount,
                  transactional: transactionalQuery,
                },
              }),
              errorMessage: "Could not execute sql query",
            });
          }}
        >
          Run Sql in conductor. Cluster: <strong>{clusterDetail?.name}</strong>
          <Box sx={{ minWidth: 120, marginTop: "16px", display: "flex" }}>
            <Box style={{ width: "100%" }}>
              <Box style={{ marginBottom: 16 }}>
                <FormControl fullWidth>
                  <TextField
                    label="Query"
                    placeholder="Select * from ...."
                    value={sqlQuery}
                    onChange={(ev) => {
                      setSqlQuery(ev.target.value);
                    }}
                    InputProps={{
                      endAdornment: sqlQuery.length > 0 && (
                        <InputAdornment
                          position="end"
                          style={{ marginRight: -8 }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => setSqlQuery("")}
                          >
                            <ClearRounded style={{ fontSize: 18 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
              </Box>
              <Box style={{ marginBottom: 16 }}>
                <FormControl fullWidth>
                  <TextField
                    label="Expected row count"
                    placeholder="10"
                    value={expectedRowCount}
                    onChange={(ev) => {
                      setExpectedRowCount(ev.target.value);
                    }}
                    InputProps={{
                      endAdornment: expectedRowCount.length > 0 && (
                        <InputAdornment
                          position="end"
                          style={{ marginRight: -8 }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => setExpectedRowCount("")}
                          >
                            <ClearRounded style={{ fontSize: 18 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
              </Box>
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 16,
                }}
              >
                <FormControlLabel
                  label="Transactional query"
                  control={
                    <Checkbox
                      size="small"
                      checked={transactionalQuery}
                      onChange={(e) => setTransactionalQuery(e.target.checked)}
                    />
                  }
                />
              </Box>
            </Box>
          </Box>
        </AlertDialog>

        <AlertDialog
          maxWidth="sm"
          textTitle="Run kubectl command"
          textContent=""
          open={selectedActionType === CLUSTER_ACTION_TYPE.KUBECTL_UNRESTRICTED}
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();
            // @ts-ignore
            kubectlUnrestrictedAction.mutate({
              method: "post",
              path: `/agent/command`,
              body: JSON.stringify({
                clusterName: clusterDetail?.name,
                command: CLUSTER_ACTION_TYPE.KUBECTL_UNRESTRICTED,
                parameters: {
                  unrestrictedCommand: unrestrictedCommand,
                },
              }),
              errorMessage: "Could not execute kubectl command",
            });
          }}
        >
          Run Kubectl command. Cluster: <strong>{clusterDetail?.name}</strong>
          <Box sx={{ minWidth: 120, marginTop: "16px", display: "flex" }}>
            <Box style={{ width: "100%" }}>
              <Box style={{ marginBottom: 16 }}>
                <FormControl fullWidth>
                  <TextField
                    label="Command (do not type 'kubectl')"
                    placeholder="get pods -n someNamespace"
                    value={unrestrictedCommand}
                    onChange={(ev) => {
                      setUnrestrictedCommand(ev.target.value);
                    }}
                    InputProps={{
                      endAdornment: unrestrictedCommand.length > 0 && (
                        <InputAdornment
                          position="end"
                          style={{ marginRight: -8 }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => setUnrestrictedCommand("")}
                          >
                            <ClearRounded style={{ fontSize: 18 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
              </Box>
            </Box>
          </Box>
        </AlertDialog>

        <AlertDialog
          maxWidth="sm"
          textTitle="Pull Pod Logs"
          textContent=""
          open={selectedActionType === CLUSTER_ACTION_TYPE.PULL_LOGS}
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();
            // @ts-ignore
            pullLogsAction.mutate({
              method: "post",
              path: `/agent/command`,
              body: JSON.stringify({
                clusterName: clusterDetail?.name,
                command: CLUSTER_ACTION_TYPE.PULL_LOGS,
                parameters: {
                  grep,
                  startLine: (Number.parseInt(startLine) || 0) + 1,
                  podName,
                  lines: Number.parseInt(lines) || 100,
                  contextFlag,
                  contextLength,
                  fetchOption,
                },
              }),
              errorMessage: "Could not pull pod logs:",
            });
          }}
        >
          Pull logs from the cluster <strong>{clusterDetail?.name}</strong>
          <Box sx={{ minWidth: 120, marginTop: "16px", display: "flex" }}>
            <Box style={{ width: "100%" }}>
              <Box style={{ marginBottom: 16 }}>
                <FormControl fullWidth>
                  <TextField
                    label="Pod Name"
                    placeholder="Pod Name"
                    value={podName}
                    onChange={(ev) => {
                      handleChangePodName(ev.target.value);
                    }}
                  />
                </FormControl>
              </Box>
              <Box style={{ marginBottom: 16 }}>
                <FormControl fullWidth>
                  <InputLabel id="fetch-option-label">Fetching</InputLabel>
                  <Select
                    labelId="fetch-option-label"
                    id="fetch-option-select"
                    value={fetchOption}
                    label="Fetching"
                    onChange={(ev) => {
                      setFetchOption(ev.target.value);
                      setStartLine("");
                    }}
                  >
                    {fetchOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box style={{ marginBottom: 16 }}>
                <FormControl fullWidth>
                  <TextField
                    label="Lines (up to 1000)"
                    placeholder="How many lines will be fetched"
                    value={lines}
                    inputProps={{
                      inputMode: "numeric",
                      type: "number",
                      min: 1,
                      max: 1000,
                      pattern: INTEGER_AT_LEAST_ONE_NUMBER,
                    }}
                    onChange={(ev) => {
                      setLines(ev.target.value.replace(/[^\d.-]/g, ""));
                    }}
                  />
                </FormControl>
              </Box>
              {fetchOption === "head" && (
                <Box style={{ marginBottom: 16 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Skipping"
                      placeholder="How many lines to skip"
                      inputProps={{
                        inputMode: "numeric",
                        type: "number",
                        min: 0,
                        pattern: INTEGER_AT_LEAST_ONE_NUMBER,
                      }}
                      value={startLine}
                      onChange={(ev) => {
                        setStartLine(ev.target.value.replace(/[^\d.-]/g, ""));
                      }}
                    />
                  </FormControl>
                </Box>
              )}
              <Box style={{ marginBottom: 16 }}>
                <FormControl fullWidth>
                  <TextField
                    label="Grep"
                    placeholder="Grep"
                    value={grep}
                    onChange={(ev) => {
                      setGrep(ev.target.value.trim());
                    }}
                    InputProps={{
                      endAdornment: grep.length > 0 && (
                        <InputAdornment
                          position="end"
                          style={{ marginRight: -8 }}
                        >
                          <IconButton size="small" onClick={() => setGrep("")}>
                            <ClearRounded style={{ fontSize: 18 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
              </Box>
              {grep.length > 0 && (
                <Box style={{ marginBottom: 16 }}>
                  <FormControl fullWidth>
                    <InputLabel id="context-flag-label">
                      With context
                    </InputLabel>
                    <Select
                      labelId="context-flag-label"
                      id="context-flag-select"
                      value={contextFlag}
                      label="With context"
                      onChange={(ev) => {
                        setContextFlag(ev.target.value);
                      }}
                    >
                      {contextFlagOptions.map((flag) => (
                        <MenuItem key={flag.value} value={flag.value}>
                          {flag.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              {grep.length > 0 && contextFlag !== "N" && (
                <Box style={{ marginBottom: 16 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Context Length"
                      placeholder="Context Length"
                      inputProps={{
                        inputMode: "numeric",
                        type: "number",
                        min: 1,
                        pattern: INTEGER_AT_LEAST_ONE_NUMBER,
                      }}
                      value={contextLength}
                      onChange={(ev) => {
                        setContextLength(ev.target.value);
                      }}
                    />
                  </FormControl>
                </Box>
              )}
            </Box>
          </Box>
        </AlertDialog>

        <AddEKSToAgentDialog
          open={selectedActionType === CLUSTER_ACTION_TYPE.ADD_EKS_ADMIN}
          closeDialog={closeDialog}
          clusterName={clusterName}
          clusterDetail={clusterDetail as CustomerEnvironment}
          showError={showError}
          showSuccess={showSuccess}
        />

        <AlertDialog
          textTitle="Deploying Metrics Proxy Confirmation"
          textContent={
            <>
              Please confirm if you want to proceed to deploy metrics-proxy for
              this cluster&nbsp;
              <strong className="danger-text">{clusterDetail?.name}</strong>.
            </>
          }
          open={selectedActionType === CLUSTER_ACTION_TYPE.DEPLOY_METRICS_PROXY}
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();
            handleDeployMetrics();
          }}
        />

        <AlertDialog
          textTitle="Deploying Debugger Pod"
          textContent={
            <>
              Please confirm if you want to proceed to deploy pod for this
              cluster&nbsp;
              <strong className="danger-text">{clusterDetail?.name}</strong>.
            </>
          }
          open={selectedActionType === CLUSTER_ACTION_TYPE.DEPLOY_DEBUGGER_POD}
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();
            handleDeployDebuggerPod();
          }}
        />

        <UploadCertificateDialog
          open={selectedActionType === CLUSTER_ACTION_TYPE.UPLOAD_CERTIFICATE}
          closeDialog={closeDialog}
          clusterName={clusterDetail?.name || ""}
        />

        <AlertDialog
          textTitle="Get Pod Names"
          textContent="Once the command is executed successfully, the pod names will be available in the Actions History."
          open={selectedActionType === CLUSTER_ACTION_TYPE.GET_PODS_NAMES}
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();
            // @ts-ignore
            getPodNamesAction.mutate({
              method: "post",
              path: `/agent/command`,
              body: JSON.stringify({
                clusterName: clusterDetail?.name,
                command: CLUSTER_ACTION_TYPE.GET_PODS_NAMES,
                parameters: {},
              }),
              errorMessage: "Pod names fetch failed:",
            });
          }}
        />

        <AlertDialog
          textTitle="Perform Health Check"
          textContent="Once the command is executed successfully, the health of the pod will be available in the Actions History."
          open={selectedActionType === CLUSTER_ACTION_TYPE.HEALTH_CHECK}
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();
            // @ts-ignore
            healthCheckAction.mutate({
              method: "post",
              path: `/agent/command`,
              body: JSON.stringify({
                clusterName: clusterDetail?.name,
                command: CLUSTER_ACTION_TYPE.HEALTH_CHECK,
                parameters: {},
              }),
              errorMessage: "Health check failed:",
            });
          }}
        />

        <AlertDialog
          maxWidth="sm"
          textTitle="MultiRegion - Peer"
          textContent="Pair this cluster with another for active-passive replication. One cluster is PRIMARY (writable); the other becomes a read-only REPLICA that syncs from it. Both must be on conductor 5.5+."
          open={
            selectedActionType === CLUSTER_ACTION_TYPE.MAKE_MULTIREGION_PEER
          }
          // Submitting an empty peer name only produces a server-side refusal.
          isConfirmDisabled={
            peerCluster.trim() === "" || multiRegionName.trim() === ""
          }
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();
            // @ts-ignore
            multiRegionAction.mutate({
              method: "post",
              path: `/agent/command`,
              body: JSON.stringify({
                clusterName: clusterDetail?.name,
                command: CLUSTER_ACTION_TYPE.MAKE_MULTIREGION_PEER,
                parameters: {
                  peerCluster,
                  primaryCluster: peerThisIsPrimary
                    ? clusterDetail?.name
                    : peerCluster,
                  multiRegionName: multiRegionName.trim(),
                },
              }),
              errorMessage: "Could not peer clusters:",
            });
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Autocomplete
              id="select-peer-cluster"
              freeSolo
              options={peerOptions.map((cluster) => cluster.name)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Peer cluster name"
                  size="small"
                  helperText={
                    peerOptions.length === 0
                      ? "No eligible peers found — standalone clusters on conductor 5.5+ only. You can still type a name."
                      : undefined
                  }
                />
              )}
              value={peerCluster}
              onInputChange={(_event, value) => setPeerCluster(value ?? "")}
              onChange={(_event, value) => setPeerCluster(value ?? "")}
              fullWidth
            />
            <TextField
              id="multi-region-name"
              label="MultiRegion name"
              size="small"
              value={multiRegionName}
              onChange={(e) => setMultiRegionName(e.target.value)}
              placeholder="acme-prod.orkesconductor.com"
              helperText="The hostname your clients will use. It follows whichever cluster is the active writer, so it must be a NEW name — not either cluster's own hostname. Changing it later is MultiRegion - Rename Endpoint, which does not take the pair down."
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={peerThisIsPrimary}
                  onChange={(e) => setPeerThisIsPrimary(e.target.checked)}
                />
              }
              label={`This cluster (${clusterDetail?.name}) is the PRIMARY`}
            />
          </Box>
        </AlertDialog>

        <AlertDialog
          maxWidth="sm"
          textTitle="MultiRegion - Promote to Primary"
          textContent={`Promote this cluster (${clusterDetail?.name}) to PRIMARY and fence its current primary to REPLICA. Refused if this cluster is already primary, not peered, or not caught up.`}
          open={selectedActionType === CLUSTER_ACTION_TYPE.PROMOTE_TO_PRIMARY}
          // Flips which region accepts writes: 2-5 minutes to converge, with a measured window where
          // neither side answered as PRIMARY. Too disruptive for a single click.
          isInputConfirmation
          valueToBeDeleted={clusterDetail?.name}
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();
            // @ts-ignore
            multiRegionAction.mutate({
              method: "post",
              path: `/agent/command`,
              body: JSON.stringify({
                clusterName: clusterDetail?.name,
                command: CLUSTER_ACTION_TYPE.PROMOTE_TO_PRIMARY,
                parameters: { peerCluster: clusterDetail?.peerClusterName },
              }),
              errorMessage: "Could not promote cluster:",
            });
          }}
        >
          <Box sx={{ mt: 1 }}>
            Promote <b>{clusterDetail?.name}</b> to PRIMARY and fence its
            current primary <b>{clusterDetail?.peerClusterName}</b> to REPLICA?
          </Box>
        </AlertDialog>

        <AlertDialog
          maxWidth="sm"
          textTitle="MultiRegion - Unpeer"
          textContent="Return both clusters to standalone/writable (removes the replication relationship)."
          open={selectedActionType === CLUSTER_ACTION_TYPE.UNPEER_CLUSTERS}
          // Not a cheap undo: re-peering mints a new shared key and starts a fresh backfill, and
          // whatever the replica had not yet pulled is abandoned.
          isInputConfirmation
          valueToBeDeleted={clusterDetail?.name}
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();
            // @ts-ignore
            multiRegionAction.mutate({
              method: "post",
              path: `/agent/command`,
              body: JSON.stringify({
                clusterName: clusterDetail?.name,
                command: CLUSTER_ACTION_TYPE.UNPEER_CLUSTERS,
                parameters: { peerCluster: clusterDetail?.peerClusterName },
              }),
              errorMessage: "Could not unpeer clusters:",
            });
          }}
        >
          <Box sx={{ mt: 1 }}>
            Unpeer <b>{clusterDetail?.name}</b> and{" "}
            <b>{clusterDetail?.peerClusterName}</b> — both return to
            standalone/writable.
          </Box>
        </AlertDialog>

        <AlertDialog
          maxWidth="sm"
          textTitle="MultiRegion - Rename Endpoint"
          textContent="Move the pair to a new multiregion hostname. The new name is stood up alongside the old one, so the pair keeps serving throughout."
          open={
            selectedActionType ===
            CLUSTER_ACTION_TYPE.RENAME_MULTIREGION_ENDPOINT
          }
          isConfirmDisabled={newMultiRegionName.trim() === ""}
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();
            // @ts-ignore
            multiRegionAction.mutate({
              method: "post",
              path: `/agent/command`,
              body: JSON.stringify({
                clusterName: clusterDetail?.name,
                command: CLUSTER_ACTION_TYPE.RENAME_MULTIREGION_ENDPOINT,
                // The name being REPLACED is deliberately not sent: the server reads it from the
                // cluster row, because a caller who could supply both could aim the retire half at
                // another pair's endpoint.
                parameters: {
                  peerCluster: clusterDetail?.peerClusterName,
                  newMultiRegionName: newMultiRegionName.trim(),
                },
              }),
              errorMessage: "Could not rename the multiregion endpoint:",
            });
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Box>
              Rename the endpoint for <b>{clusterDetail?.name}</b> and{" "}
              <b>{clusterDetail?.peerClusterName}</b>, currently{" "}
              <b>{clusterDetail?.multiRegionName}</b>.
            </Box>
            <TextField
              id="new-multi-region-name"
              label="New MultiRegion name"
              size="small"
              value={newMultiRegionName}
              onChange={(e) => setNewMultiRegionName(e.target.value)}
              placeholder="acme-prod.orkesconductor.com"
              helperText="Must be a NEW name no other pair holds, and not either cluster's own hostname. Clients keep using the old name until you move them."
              fullWidth
            />
          </Box>
        </AlertDialog>

        <AlertDialog
          maxWidth="sm"
          textTitle="Replace Cluster Domain"
          textContent="Move this cluster to a new hostname. The new one is stood up and the old one retired."
          open={
            selectedActionType === CLUSTER_ACTION_TYPE.REPLACE_CLUSTER_DOMAIN
          }
          isConfirmDisabled={newClusterDomain.trim() === ""}
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();
            // @ts-ignore
            multiRegionAction.mutate({
              method: "post",
              path: `/agent/command`,
              body: JSON.stringify({
                clusterName: clusterDetail?.name,
                command: CLUSTER_ACTION_TYPE.REPLACE_CLUSTER_DOMAIN,
                // Only the new hostname: the one being replaced comes from the cluster row, so a
                // caller cannot aim the retire half at a hostname belonging to something else.
                parameters: { newDomain: newClusterDomain.trim() },
              }),
              errorMessage: "Could not replace the cluster domain:",
            });
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Box>
              Replace the hostname for <b>{clusterDetail?.name}</b>, currently{" "}
              <b>{clusterDetail?.domainUrl}</b>.
            </Box>
            <TextField
              id="new-cluster-domain"
              label="New cluster domain"
              size="small"
              value={newClusterDomain}
              onChange={(e) => setNewClusterDomain(e.target.value)}
              placeholder="acme.orkesconductor.com"
              helperText="Clients on the old hostname will stop reaching this cluster once it is retired."
              fullWidth
            />
          </Box>
        </AlertDialog>

        <AlertDialog
          textTitle="Get Deployments Info"
          textContent="Once the command is executed successfully, the Deployments Info be available in the Actions History."
          open={selectedActionType === CLUSTER_ACTION_TYPE.GET_DEPLOYMENTS_INFO}
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();
            // @ts-ignore
            getDeploymentsInfoAction.mutate({
              method: "post",
              path: `/agent/command`,
              body: JSON.stringify({
                clusterName: clusterDetail?.name,
                command: CLUSTER_ACTION_TYPE.GET_DEPLOYMENTS_INFO,
                parameters: {},
              }),
              errorMessage: "Deployment info fetch failed:",
            });
          }}
        />

        <AlertDialog
          textTitle="Download all logs from Pod"
          textContent="Once the command is executed successfully, will be able to download all logs from Pod."
          open={
            selectedActionType === CLUSTER_ACTION_TYPE.DOWNLOAD_ALL_POD_LOGS
          }
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();
            // @ts-ignore
            downloadAllPodLogsAction.mutate({
              method: "post",
              path: `/agent/command`,
              body: JSON.stringify({
                clusterName: clusterDetail?.name,
                command: CLUSTER_ACTION_TYPE.DOWNLOAD_ALL_POD_LOGS,
                parameters: {
                  podName,
                },
              }),
              errorMessage: "Download all Pod logs failed:",
            });
            handleChangePodName("");
          }}
        >
          <PodSelector />
        </AlertDialog>

        <AlertDialog
          textTitle="Download thread dump"
          textContent="Once the command is executed successfully, will be able to download thread dump."
          open={selectedActionType === CLUSTER_ACTION_TYPE.DOWNLOAD_THREAD_DUMP}
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();
            // @ts-ignore
            downloadThreadDumpAction.mutate({
              method: "post",
              path: `/agent/command`,
              body: JSON.stringify({
                clusterName: clusterDetail?.name,
                command: CLUSTER_ACTION_TYPE.DOWNLOAD_THREAD_DUMP,
                parameters: {
                  podName,
                },
              }),
              errorMessage: "Download thread fetch failed:",
            });
            handleChangePodName("");
          }}
        >
          <PodSelector />
        </AlertDialog>

        <AlertDialog
          textTitle="Get Heap Dump"
          textContent="Once the command is executed successfully, the Heap dump will be available in the Actions History."
          open={selectedActionType === CLUSTER_ACTION_TYPE.DOWNLOAD_HEAP_DUMP}
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();
            // @ts-ignore
            getHeapDumpAction.mutate({
              method: "post",
              path: `/agent/command`,
              body: JSON.stringify({
                clusterName: clusterDetail?.name,
                command: CLUSTER_ACTION_TYPE.DOWNLOAD_HEAP_DUMP,
                parameters: {
                  podName,
                },
              }),
              errorMessage: "Heap dump fetch failed:",
            });
            handleChangePodName("");
          }}
        >
          <Box sx={{ minWidth: 120, marginTop: "16px", display: "flex" }}>
            <Box style={{ width: "100%" }}>
              <Box style={{ marginBottom: 16 }}>
                <Autocomplete
                  id="select-pod-name"
                  freeSolo
                  options={podsNames.filter(
                    (name: string) =>
                      name?.includes("conductor") || name?.includes("workers")
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Pod Name" />
                  )}
                  value={podName}
                  onInputChange={(_event, value) => {
                    handleChangePodName(value ?? "");
                  }}
                  onChange={(_event, value) => {
                    handleChangePodName(value ?? "");
                  }}
                />
              </Box>
            </Box>
          </Box>
        </AlertDialog>

        <AlertDialog
          textTitle="Get Top Output From Pod"
          textContent="Once the command is executed successfully, the Top Output Info be available in the Actions History."
          open={
            selectedActionType === CLUSTER_ACTION_TYPE.GET_TOP_OUTPUT_FROM_POD
          }
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();

            // @ts-ignore
            getTopOutputFromPodAction.mutate({
              method: "post",
              path: `/agent/command`,
              body: JSON.stringify({
                clusterName: clusterDetail?.name,
                command: CLUSTER_ACTION_TYPE.GET_TOP_OUTPUT_FROM_POD,
                parameters: {
                  podName,
                },
              }),
              errorMessage: "Top Output info fetch failed:",
            });
            handleChangePodName("");
          }}
        >
          <Box sx={{ minWidth: 120, marginTop: "16px", display: "flex" }}>
            <Box style={{ width: "100%" }}>
              <Box style={{ marginBottom: 16 }}>
                <Autocomplete
                  id="select-pod-name"
                  freeSolo
                  options={podsData.map((option: Pod) => option.name)}
                  renderInput={(params) => (
                    <TextField {...params} label="Pod Name" />
                  )}
                  value={podName}
                  onInputChange={(_event, value) => {
                    handleChangePodName(value ?? "");
                  }}
                  onChange={(_event, value) => {
                    handleChangePodName(value ?? "");
                  }}
                />
              </Box>
            </Box>
          </Box>
        </AlertDialog>

        <RolloutRestartDialog
          textTitle="Rollout Restart"
          textContent={""}
          open={selectedActionType === CLUSTER_ACTION_TYPE.ROLLOUT_RESTART}
          setOpen={closeDialog}
          handleConfirm={async () => {
            closeDialog();
            // @ts-ignore
            await getDeploymentsInfoAction.mutateAsync({
              method: "post",
              path: `/agent/command`,
              body: JSON.stringify({
                clusterName: clusterDetail?.name,
                command: CLUSTER_ACTION_TYPE.ROLLOUT_RESTART,
                parameters: {
                  resource: selectedRestartResourceType.value?.toUpperCase(),
                },
              }),
            });
            // Best effort, and deliberately not awaited into the user's path: the restart itself has
            // already been accepted, so a failure to refresh should not surface as a failed restart.
            const refreshPods = {
              method: "post",
              path: `/agent/command`,
              body: JSON.stringify({
                clusterName: clusterDetail?.name,
                command: CLUSTER_ACTION_TYPE.GET_DEPLOYMENTS_INFO,
                parameters: {},
              }),
            };
            // @ts-ignore
            refreshPodsAfterRestartAction.mutateAsync(refreshPods).catch(() => {
              /* the tab's Refresh Pods button remains the manual fallback */
            });
          }}
          isConfirmDisabled={!selectedRestartResourceType}
          selectedResourceType={selectedRestartResourceType}
          setSelectedResourceType={setSelectedRestartResourceType}
          resourceTypeOptions={rolloutRestartResourceTypes}
        />

        <ChangeEnvironmentTypeDialog
          clusterName={clusterDetail?.name || ""}
          open={
            selectedActionType === CLUSTER_ACTION_TYPE.CHANGE_ENVIRONMENT_TYPE
          }
          setOpen={closeDialog}
          environmentType={clusterDetail?.environmentType || "PRODUCTION"}
          refetchCluster={refetchClusterDetail}
        />

        <ChangeTelemetryDialog
          open={
            selectedActionType === CLUSTER_ACTION_TYPE.CHANGE_TELEMETRY_STATE
          }
          closeDialog={closeDialog}
          currentTelemetryState={clusterDetail?.telemetryState ?? "NORMAL"}
          clusterName={clusterDetail?.name || ""}
          refetchCluster={refetchClusterDetail}
        />

        <ApiGatewayToggleDialog
          clusterDetail={clusterDetail}
          open={selectedActionType === CLUSTER_ACTION_TYPE.ENABLE_API_GATEWAY}
          setOpen={closeDialog}
          refetchCluster={refetchClusterDetail}
          isEnabling={true}
        />

        <ApiGatewayToggleDialog
          clusterDetail={clusterDetail}
          open={selectedActionType === CLUSTER_ACTION_TYPE.DISABLE_API_GATEWAY}
          setOpen={closeDialog}
          refetchCluster={refetchClusterDetail}
          isEnabling={false}
        />

        <UpdateKubernetesDialog
          clusterDetail={clusterDetail}
          open={selectedActionType === CLUSTER_ACTION_TYPE.UPDATE_KUBERNETES}
          setOpen={closeDialog}
          refetchCluster={refetchClusterDetail}
        />

        <AlertDialog
          textTitle="Generate Agent Role Update Script"
          textContent={
            <>
              This will generate a script to update the agent role with the
              necessary permissions for metrics collection. The script will be
              downloaded to your computer for cluster&nbsp;
              <strong className="danger-text">{clusterDetail?.name}</strong>.
            </>
          }
          open={
            selectedActionType ===
            CLUSTER_ACTION_TYPE.GENERATE_AGENT_ROLE_UPDATE_SCRIPT
          }
          setOpen={closeDialog}
          handleConfirm={() => {
            closeDialog();
            handleGenerateAgentRoleUpdateScript();
          }}
        />
      </>
    </>
  );
}

export default ActionTypeSelector;
